import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} ShopifyVariant
 * @property {number} id
 * @property {string} title
 * @property {number} price
 * @property {number} [compare_at_price]
 * @property {Object} [featured_image]
 * @property {string} [featured_image.src]
 * @property {string} [base_color]
 * @property {Object} [metafields]
 */

/**
 * @typedef {Object} ShopifyProduct
 * @property {string} handle
 * @property {string} title
 * @property {ShopifyVariant[]} variants
 */

/**
 * Handles variant-based filtering for collection pages
 * When a base color filter is selected, shows each matching variant as a separate product card
 * 
 * @extends {Component}
 */
class VariantFilterComponent extends Component {
    #moneyFormat = '${{amount}}';

    connectedCallback() {
        super.connectedCallback();

        // Get money format from Shopify
        // @ts-ignore - Shopify global is added by Shopify
        this.#moneyFormat = (window.Shopify && window.Shopify.money_format) || this.dataset.moneyFormat || '${{amount}}';

        // Listen for filter updates BEFORE they trigger section render
        document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate, true);

        // Check initial state
        this.#checkAndApplyVariantFilter();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    }

    /**
     * Handles filter update events
     * @param {Event} event - The filter update event
     */
    #handleFilterUpdate = (event) => {
        // Wait for section to re-render, then apply variant filtering
        setTimeout(() => {
            this.#checkAndApplyVariantFilter();
        }, 500);
    };

    /**
     * Checks URL parameters and applies variant filtering if base color filter is active
     */
    #checkAndApplyVariantFilter() {
        const urlParams = new URLSearchParams(window.location.search);
        const baseColorFilter = this.#getBaseColorFilter(urlParams);

        // Clean up any existing variant cards
        this.#cleanupVariantCards();

        if (baseColorFilter) {
            // If base color filter is active, show matching variants
            this.#applyVariantFilter(baseColorFilter);
        } else {
            // If no base color filter, show ALL color variants as separate cards
            this.#expandAllColorVariants();
        }
    }

    /**
     * Removes all variant cards and restores original product cards
     */
    #cleanupVariantCards() {
        const productGrid = document.querySelector('.product-grid__list');
        if (!productGrid) return;

        // Remove all variant cards (cards with data-variant-id attribute)
        const variantCards = productGrid.querySelectorAll('[data-variant-id]');
        variantCards.forEach(card => card.remove());

        // Show all original product cards
        const productCards = productGrid.querySelectorAll('.product-grid__item[data-product-id]');
        productCards.forEach(card => {
            if (card instanceof HTMLElement) {
                card.style.display = '';
            }
        });
    }

    /**
     * Expands all products to show each color variant as a separate card
     * Shows ALL variants (including same color with different sizes)
     */
    #expandAllColorVariants() {
        const productGrid = document.querySelector('.product-grid__list');
        if (!productGrid) return;

        const productCards = productGrid.querySelectorAll('.product-grid__item[data-product-id]');
        /** @type {HTMLElement[]} */
        const newCards = [];

        for (const card of productCards) {
            if (!(card instanceof HTMLElement)) continue;

            const productHandle = card.dataset.productHandle;
            const variantColorsData = card.dataset.variantColors;

            if (!productHandle || !variantColorsData) continue;

            try {
                // Parse variant colors from data attribute
                const variants = JSON.parse(variantColorsData);

                // Get product title from the card
                const titleElement = card.querySelector('.product-card__title, .card__title, h3');
                const productTitle = titleElement ? titleElement.textContent?.trim() || '' : '';

                // Create a card for EACH variant (show all, even duplicate colors)
                for (const variant of variants) {
                    const variantCard = this.#createVariantCardFromData(card, productHandle, productTitle, variant);
                    if (variantCard) {
                        newCards.push(variantCard);
                    }
                }

                // If we created variant cards, hide the original
                if (newCards.length > 0) {
                    card.style.display = 'none';
                }
            } catch (error) {
                console.error('Error expanding product variants:', error);
            }
        }

        // Insert new variant cards
        for (const newCard of newCards) {
            productGrid.appendChild(newCard);
        }
    }

    /**
     * Gets the color from a variant's options
     * @param {any} variant - The variant object
     * @returns {string|null} The color value or null
     */
    #getVariantColor(variant) {
        if (!variant.options || !Array.isArray(variant.options)) {
            return null;
        }

        // Assuming "Color" is the first option, or find it by name
        // Since we don't have option names, we'll use the first option
        return variant.options[0] || null;
    }

    /**
     * Gets the base color filter values from URL parameters
     * @param {URLSearchParams} urlParams - The URL parameters
     * @returns {string[]|null} Array of base color filter values or null if none found
     */
    #getBaseColorFilter(urlParams) {
        /** @type {string[]} */
        const baseColors = [];

        // Check for base color filter in URL
        // The parameter name is 'filter.v.option.base-color' or similar
        for (const [key, value] of urlParams.entries()) {
            // Only match parameters that contain both 'base' and 'color'
            if (key.includes('base') && key.includes('color')) {
                // Add the value to our array if it's not already there
                if (value && !baseColors.includes(value)) {
                    baseColors.push(value);
                }
            }
        }

        return baseColors.length > 0 ? baseColors : null;
    }

    /**
     * Applies variant filtering to the product grid
     * @param {string[]} baseColors - Array of base colors to filter by
     */
    async #applyVariantFilter(baseColors) {
        if (!baseColors || baseColors.length === 0) return;

        const productGrid = document.querySelector('.product-grid__list');
        if (!productGrid) return;

        const productCards = productGrid.querySelectorAll('.product-grid__item');
        /** @type {HTMLElement[]} */
        const newCards = [];

        for (const card of productCards) {
            if (!(card instanceof HTMLElement)) continue;

            const productId = card.dataset.productId;
            const productHandle = card.dataset.productHandle;
            const variantColorsData = card.dataset.variantColors;

            if (!productId || !productHandle || !variantColorsData) continue;

            try {
                // Parse variant colors from data attribute
                const variants = JSON.parse(variantColorsData);

                // Find variants matching any of the base colors
                const matchingVariants = this.#findMatchingVariantsFromData(variants, baseColors);

                if (matchingVariants.length > 0) {
                    // Get product title from the card
                    const titleElement = card.querySelector('.product-card__title, .card__title, h3');
                    const productTitle = titleElement ? titleElement.textContent?.trim() || '' : '';

                    // Create a card for each matching variant
                    for (const variant of matchingVariants) {
                        const variantCard = this.#createVariantCardFromData(card, productHandle, productTitle, variant);
                        if (variantCard) {
                            newCards.push(variantCard);
                        }
                    }

                    // Hide the original card
                    card.style.display = 'none';
                }
            } catch (error) {
                console.error('Error processing product:', error);
            }
        }

        // Insert new variant cards
        for (const newCard of newCards) {
            productGrid.appendChild(newCard);
        }
    }

    /**
     * Finds variants matching the base colors from data attributes
     * Checks both base_color metafield and Color option
     * @param {any[]} variants - Array of variant data
     * @param {string[]} baseColors - Array of base colors to match
     * @returns {any[]} Array of matching variants
     */
    #findMatchingVariantsFromData(variants, baseColors) {
        if (!Array.isArray(variants) || !Array.isArray(baseColors) || baseColors.length === 0) {
            return [];
        }

        return variants.filter((variant) => {
            // Try to get color from base_color metafield first
            let variantColor = variant.base_color;

            // If no base_color, get from options (Color is usually first option)
            if (!variantColor) {
                variantColor = this.#getVariantColor(variant);
            }

            if (!variantColor) return false;

            // Handle comma-separated colors (e.g., "Mavi, Mor" -> ["Mavi", "Mor"])
            const variantColors = String(variantColor)
                .split(',')
                .map(color => color.trim().toLowerCase());

            // Check if any of the variant's colors match any of the selected filters
            return baseColors.some(filterColor =>
                variantColors.includes(filterColor.toLowerCase())
            );
        });
    }


    /**
     * Creates a product card for a specific variant using data attributes
     * @param {HTMLElement} originalCard - The original product card element
     * @param {string} productHandle - The product handle
     * @param {string} productTitle - The product title
     * @param {any} variant - The variant data from data attribute
     * @returns {HTMLElement|null} The new variant card element
     */
    #createVariantCardFromData(originalCard, productHandle, productTitle, variant) {
        try {
            // Clone the original card
            const newCard = /** @type {HTMLElement} */ (originalCard.cloneNode(true));

            // Update card attributes
            newCard.dataset.variantId = String(variant.id);
            newCard.style.display = '';

            // Update the product link to point to the variant
            const productLink = newCard.querySelector('.product-card__link');
            if (productLink instanceof HTMLAnchorElement) {
                productLink.href = `/products/${productHandle}?variant=${variant.id}`;
            }

            // Update the product title to show color name only
            const titleElement = newCard.querySelector('.product-card__title, .card__title, h3');
            if (titleElement) {
                // Get the color from variant (first option is color)
                const variantColor = this.#getVariantColor(variant);
                if (variantColor) {
                    titleElement.textContent = `${productTitle} - ${variantColor}`;
                } else {
                    titleElement.textContent = `${productTitle} - ${variant.title}`;
                }
            }

            // Update the product image to show variant image
            if (variant.featured_image) {
                const imageElement = newCard.querySelector('img');
                if (imageElement instanceof HTMLImageElement) {
                    imageElement.src = variant.featured_image;
                    imageElement.srcset = '';
                    imageElement.alt = variant.title;
                }
            }

            // Update the price
            const priceElement = newCard.querySelector('.price__regular .price-item--regular');
            if (priceElement && variant.price) {
                priceElement.textContent = this.#formatMoney(variant.price);
            }

            // Update compare at price if exists
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                const compareElement = newCard.querySelector('.price__compare .price-item--regular');
                if (compareElement) {
                    compareElement.textContent = this.#formatMoney(variant.compare_at_price);
                }
            }

            return newCard;
        } catch (error) {
            console.error('Error creating variant card:', error);
            return null;
        }
    }

    /**
     * Formats price in cents to currency string using Shopify's money format
     * @param {number} cents - Price in cents
     * @returns {string} Formatted price
     */
    #formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);

        // Use Shopify's money format if available
        if (this.#moneyFormat) {
            return this.#moneyFormat
                .replace('{{amount}}', amount)
                .replace('{{amount_no_decimals}}', Math.floor(cents / 100).toString())
                .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
        }

        return `$${amount}`;
    }
}

if (!customElements.get('variant-filter-component')) {
    customElements.define('variant-filter-component', VariantFilterComponent);
}
